// src/admin/components/doctors/Doctors.jsx

import React, { useMemo, useState, useEffect } from 'react';
import {
  Card,
  Table,
  Space,
  Input,
  Select,
  Button,
  Tag,
  Tooltip,
  Avatar,
  Typography,
  message,
  Modal,
  Image,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  CheckOutlined,
  CloseOutlined,
  CameraOutlined,
  CalendarOutlined,
} from '@ant-design/icons';

// ✅ Hook + helper depuis useAdmin
import { useDoctors, buildDoctorImageUrl } from '../../hooks/useAdmin';

const { Text } = Typography;

const STATUS_COLORS = {
  pending: 'gold',
  confirmed: 'green',
  approved: 'green',
  rejected: 'red',
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'rejected', label: 'Rejected' },
];

// ✅ Petit composant pour afficher les images avec la bonne URL
const ImagePreview = ({ src, title = 'Image' }) => {
  const url = buildDoctorImageUrl(src);
  if (!url) return <Text type="secondary">—</Text>;

  return (
    <Tooltip title={`Cliquez pour voir: ${title}`}>
      <Image
        src={url}
        alt={title}
        width={50}
        height={50}
        style={{ objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
        preview={{
          mask: <CameraOutlined style={{ fontSize: 16, color: '#fff' }} />,
        }}
      />
    </Tooltip>
  );
};

export default function Doctors() {
  // ── Redux via hook admin
  const { items, isLoading, confirmDoctor, rejectDoctor, refetch } = useDoctors();

  // ── Filtres locaux
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(undefined);
  const [specialty, setSpecialty] = useState(undefined);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 8 });

  // ── Modal rejet
  const [rejectModal, setRejectModal] = useState({ open: false, id: null });
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  // ── Modal détails complets
  const [detailsModal, setDetailsModal] = useState({ open: false, doctor: null });

  // Quand filtres changent, on revient page 1
  useEffect(() => {
    setPagination((p) => ({ ...p, current: 1 }));
  }, [search, status, specialty]);

  const specialties = useMemo(
    () =>
      Array.from(new Set(items.map((d) => d.specialty).filter(Boolean))).sort(),
    [items]
  );

  const filtered = useMemo(() => {
    return items.filter((d) => {
      const searchText = [
        d.fullName,
        d.firstName,
        d.lastName,
        d.email,
        d.phone,
        d.city,
        d.specialty,
        d.country,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = !search || searchText.includes(search.toLowerCase());
      const matchesStatus = !status || d.status === status;
      const matchesSpecialty = !specialty || d.specialty === specialty;

      return matchesSearch && matchesStatus && matchesSpecialty;
    });
  }, [items, search, status, specialty]);

  // ── Handlers
  const handleConfirm = async (id) => {
    try {
      await confirmDoctor(id);
      message.success('Médecin confirmé');
    } catch (err) {
      message.error(err?.message || 'Erreur lors de la confirmation');
    }
  };

  const openRejectModal = (id) => {
    setRejectReason('');
    setRejectModal({ open: true, id });
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      message.warning('La raison est obligatoire');
      return;
    }
    setRejectLoading(true);
    try {
      await rejectDoctor(rejectModal.id, rejectReason.trim());
      message.success('Médecin rejeté');
      setRejectModal({ open: false, id: null });
    } catch (err) {
      message.error(err?.message || 'Erreur lors du rejet');
    } finally {
      setRejectLoading(false);
    }
  };

  const openDetailsModal = (doctor) => {
    setDetailsModal({ open: true, doctor });
  };

  const resetFilters = () => {
    setSearch('');
    setStatus(undefined);
    setSpecialty(undefined);
    setPagination((p) => ({ ...p, current: 1 }));
  };

  // ── Colonnes du tableau
  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      fixed: 'left',
      render: (s) => (
        <Tag color={STATUS_COLORS[s] ?? 'default'}>
          {s ? s.charAt(0).toUpperCase() + s.slice(1) : '—'}
        </Tag>
      ),
    },
    {
      title: 'Médecin',
      key: 'doctor',
      width: 240,
      sorter: (a, b) => {
        const nameA = a.fullName || `${a.firstName || ''} ${a.lastName || ''}`.trim();
        const nameB = b.fullName || `${b.firstName || ''} ${b.lastName || ''}`.trim();
        return nameA.localeCompare(nameB);
      },
      render: (_, record) => {
        const name =
          record.fullName ||
          `${record.firstName || ''} ${record.lastName || ''}`.trim();
        return (
          <Space direction="vertical" size={4}>
            <Space>
              <Avatar
                size={40}
                style={{ background: '#1890ff', color: '#fff' }}
              >
                {name
                  .split(' ')
                  .map((p) => p[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase()}
              </Avatar>
              <div>
                <Text strong style={{ display: 'block' }}>
                  {name}
                </Text>
                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  <MailOutlined /> {record.email}
                </Text>
              </div>
            </Space>
          </Space>
        );
      },
    },
    {
      title: 'Spécialité',
      dataIndex: 'specialty',
      key: 'specialty',
      width: 140,
      render: (s) =>
        s ? <Tag color="blue">{s}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Téléphone',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
      render: (p) =>
        p ? (
          <Space size={4}>
            <PhoneOutlined />
            <Text>{p}</Text>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Ville',
      dataIndex: 'city',
      key: 'city',
      width: 120,
      render: (c) =>
        c ? (
          <Space size={4}>
            <EnvironmentOutlined />
            <Text>{c}</Text>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Pays',
      dataIndex: 'country',
      key: 'country',
      width: 110,
      render: (c) => c || <Text type="secondary">—</Text>,
    },
    {
      title: 'Exp. (ans)',
      dataIndex: 'experience',
      key: 'experience',
      width: 100,
      sorter: (a, b) => (a.experience ?? 0) - (b.experience ?? 0),
      render: (y) =>
        y != null ? <Text strong>{y}</Text> : <Text type="secondary">—</Text>,
      align: 'center',
    },
    {
      title: 'Lieu de Pratique',
      dataIndex: 'practicePlace',
      key: 'practicePlace',
      width: 130,
      render: (p) =>
        p ? <Tag color="cyan">{p}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Adresse',
      dataIndex: 'clinicAddress',
      key: 'clinicAddress',
      width: 200,
      ellipsis: { showTitle: false },
      render: (a) => (
        <Tooltip title={a}>
          <Text ellipsis>{a || '—'}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Localisation',
      key: 'location',
      width: 130,
      render: (_, r) =>
        r.lat && r.lng ? (
          <Space direction="vertical" size={2}>
            <Text style={{ fontSize: 11 }}>Lat: {r.lat.toFixed(3)}</Text>
            <Text style={{ fontSize: 11 }}>Lng: {r.lng.toFixed(3)}</Text>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
      align: 'center',
    },
    {
      title: 'Licence',
      dataIndex: 'licenceImg',
      key: 'licenceImg',
      width: 90,
      render: (src) => <ImagePreview src={src} title="Licence" />,
      align: 'center',
    },
    {
      title: 'Identité',
      dataIndex: 'identityImg',
      key: 'identityImg',
      width: 90,
      render: (src) => <ImagePreview src={src} title="Identité" />,
      align: 'center',
    },
    {
      title: 'Naissance',
      dataIndex: 'birthDate',
      key: 'birthDate',
      width: 120,
      render: (d) =>
        d ? (
          <Text>
            <CalendarOutlined />{' '}
            {new Date(d).toLocaleDateString('fr-FR')}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
      align: 'center',
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            size="small"
            type="link"
            onClick={() => openDetailsModal(record)}
          >
            Détails
          </Button>
          <Tooltip title="Confirmer">
            <Button
              size="small"
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => handleConfirm(record.id)}
              disabled={
                record.status === 'confirmed' || record.status === 'approved'
              }
              style={
                record.status !== 'confirmed' && record.status !== 'approved'
                  ? { background: '#16a34a', borderColor: '#16a34a' }
                  : {}
              }
            />
          </Tooltip>
          <Tooltip title="Rejeter">
            <Button
              size="small"
              danger
              icon={<CloseOutlined />}
              onClick={() => openRejectModal(record.id)}
              disabled={record.status === 'rejected'}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        bodyStyle={{ padding: 16 }}
        title={
          <Space size={12} wrap>
            <Input
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher (nom, email, ville, spécialité...)"
              prefix={<SearchOutlined />}
              style={{ width: 320 }}
            />
            <Select
              allowClear
              placeholder="Spécialité"
              options={specialties.map((s) => ({ value: s, label: s }))}
              value={specialty}
              onChange={setSpecialty}
              style={{ width: 180 }}
            />
            <Select
              allowClear
              placeholder="Status"
              options={STATUS_OPTIONS}
              value={status}
              onChange={setStatus}
              style={{ width: 160 }}
            />
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              resetFilters();
              refetch();
            }}
          >
            Réinitialiser
          </Button>
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={filtered}
          loading={isLoading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [5, 8, 10, 20, 50],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} médecins`,
            position: ['bottomRight'],
          }}
          onChange={(pag) =>
            setPagination({ current: pag.current, pageSize: pag.pageSize })
          }
          scroll={{ x: 1500 }}
          size="small"
        />
      </Card>

      {/* Modal rejet */}
      <Modal
        title="Raison du rejet"
        open={rejectModal.open}
        onOk={handleReject}
        onCancel={() => setRejectModal({ open: false, id: null })}
        okText="Rejeter"
        cancelText="Annuler"
        okButtonProps={{ danger: true, loading: rejectLoading }}
        destroyOnHidden
      >
        <Input.TextArea
          rows={4}
          placeholder="Expliquez la raison du rejet..."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          maxLength={500}
          showCount
        />
      </Modal>

      {/* Modal détails (simplifié pour l’instant) */}
      <Modal
        title="Détails du médecin"
        open={detailsModal.open}
        footer={null}
        onCancel={() => setDetailsModal({ open: false, doctor: null })}
        width={700}
      >
        {detailsModal.doctor && (
          <pre style={{ whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(detailsModal.doctor, null, 2)}
          </pre>
        )}
      </Modal>
    </>
  );
}